import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2 } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";
import { useMembershipGate } from "@/hooks/useMembershipGate";
import { useSubUserSeatSelect, SubUserSeatDialog } from "@/hooks/useSubUserSeatSelect";

/**
 * Reusable checkout button. Wire it anywhere by passing the parameters — the
 * button POSTs to /api/payments/checkout and redirects to Stripe.
 *
 * Props:
 *   kind: "membership" | "deal" | undefined (use lookupKey directly)
 *   itemId: deal id when kind="deal"
 *   priceOptionId: one of the deal's price_options ids; omit for the default price
 *   interval: "monthly" | "yearly" when kind="membership"
 *   lookupKey: bypass kind/itemId and use a raw Stripe lookup key
 *   customerEmail: buyer's email (used for receipt)
 *   includeSubUserIds: for kind="membership" — explicit override that skips
 *     the built-in seat-selection dialog and uses this array as-is (even
 *     []). Leave undefined (the normal case) to let the button show the
 *     dialog itself and resolve the selection on click.
 *   label / testid / className / children optional
 */
export default function CheckoutButton({
    kind,
    itemId,
    priceOptionId,
    interval = "monthly",
    lookupKey,
    customerEmail,
    userId,
    promoCode,
    includeSubUserIds,
    autoStart = false,
    label = "Buy now",
    testid = "checkout-btn",
    className = "",
    children,
    ...rest
}) {
    const [loading, setLoading] = useState(false);
    // Route membership checkouts to the caller's active market so the
    // backend can pick the locally-minted Stripe price. Unset markets
    // fall back to HKD server-side, so this is safe even when a country
    // has no membership price yet.
    const { country } = useCountry();
    const { requireActiveMembership, MembershipGateDialog } = useMembershipGate();
    const { selectSeats, seatDialogProps } = useSubUserSeatSelect();

    useEffect(() => {
        // When autoStart is set (workflow runner finished), fire the
        // checkout flow once as soon as the component mounts.
        if (autoStart) go();
    }, [autoStart]);

    const go = async () => {
        // Perk purchases require an active membership — membership
        // checkout itself is exempt, otherwise no one could ever buy one.
        // The backend enforces this too (403 on /payments/checkout); this
        // is just the dialog-before-redirect UX.
        if (kind === "deal" && !requireActiveMembership()) return;

        // Membership checkout: let the member add family seats, unless the
        // caller already passed an explicit list (even []) to skip this.
        let subUserIds = includeSubUserIds;
        if (kind === "membership" && subUserIds === undefined) {
            subUserIds = await selectSeats();
        }

        setLoading(true);
        try {
            const payload = {
                origin_url: window.location.origin,
                customer_email: customerEmail || undefined,
                user_id: userId || undefined,
                quantity: 1,
            };
            if (promoCode) payload.promo_code = promoCode;
            if (subUserIds && subUserIds.length > 0)
                payload.include_sub_user_ids = subUserIds;
            if (lookupKey) payload.lookup_key = lookupKey;
            else {
                payload.kind = kind;
                if (kind === "deal") {
                    payload.item_id = itemId;
                    if (priceOptionId) payload.price_option_id = priceOptionId;
                }
                if (kind === "membership") {
                    payload.interval = interval;
                    if (country) payload.country = country;
                }
            }
            const { data } = await api.post("/payments/checkout", payload);
            if (!data.checkout_url) {
                throw new Error("Payment gateway did not return a checkout URL.");
            }
            // Persist intent so /payment/success can query status even after redirect
            try {
                sessionStorage.setItem(
                    "perx_last_checkout",
                    JSON.stringify({
                        session_id: data.session_id,
                        product_name: data.product_name,
                        amount: data.amount,
                        currency: data.currency,
                    })
                );
            } catch (_e) {
                // storage disabled — no-op
            }
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatApiError(err));
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                {...rest}
                data-testid={testid}
                onClick={go}
                disabled={loading}
                className={
                    className ||
                    "rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                }
            >
                {loading ? (
                    <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Redirecting…
                    </>
                ) : (
                    <>
                        <CreditCard size={16} className="mr-2" />
                        {children || label}
                    </>
                )}
            </Button>
            <MembershipGateDialog />
            <SubUserSeatDialog {...seatDialogProps} />
        </>
    );
}
